/**
 * Fetches product data for the given product id from the catalog API.
 *
 * @param id - The product identifier used as the `productId` query filter
 * @returns The parsed JSON response containing product data, or `undefined` if the request fails
 */
export async function getProductDataById(id: any) {
  try {
    const response = await fetch(
      `/api/catalog_system/pub/products/search?fq=productId:${id}`
    );

    return await response.json();
  } catch (e) {
    console.log('[DT] Erro ao buscar dados do produto', e);
  }
}

export function resizeVtexImage(url: string, width = 500, height = 500) {
  return url?.replace(
    /(\/arquivos\/ids\/\d+)-\d+-\d+\//,
    `$1-${width}-${height}/`
  );
}

export function isPDP(url: string) {
  try {
    const { pathname } = new URL(url, window.location.origin);
    return /\/p\/?$/.test(pathname);
  } catch {
    return false;
  }
}

export function extractRefIdFromUrl(url: string | null) {
  if (!url) return null;

  const path = (() => {
    try { return new URL(url, location.origin).pathname }
    catch { return url.split('?')[0] }
  })();

  const match = decodeURIComponent(path).match(/(\d+-\d+)(?=\/p|$)/);
  return match ? match[1].replace('-', '_') : null;
}

export async function getProductByRefId(url: string | null) {
  const refId = extractRefIdFromUrl(url);
  if (!refId) return console.error('Não foi possível extrair o RefId da URL'), null;

  try {
    const res = await fetch(`/api/catalog_system/pub/products/search?fq=alternateIds_RefId:${refId}`);
    if (!res.ok) throw new Error(`Erro na API: ${res.status}`);

    const products = await res.json();
    return products?.[0] || null;
  } catch (err) {
    console.error('Erro ao buscar dados do produto:', err);
    return null;
  }
}

export function getItemShippingTier(orderForm: any, item: any) {
  const info = orderForm?.shippingData?.logisticsInfo?.find(
    (li: any) => li.itemId === item.id
  );

  const selectedSla = info?.slas?.find((sla: any) => sla.id === info.selectedSla);
  return selectedSla?.pickupStoreInfo?.friendlyName
    ? `retirada em loja: ${selectedSla.pickupStoreInfo.friendlyName}`
    : selectedSla?.name
      ? `Receba em casa: ${selectedSla.name}`
      : null;
}

export async function getItemSku(item: any) {
  const product = await getProductDataById(item.productId || item.productID || item.item_group_id);

  if (!product.length) return null;

  return product[0].items.find((i: any) => i.name === item.skuName)?.itemId ?? null;
}

export function getDiscount(item: any) {
  const strategies = [
    // 1. listPrice - sellingPrice
    () => {
      if (item?.listPrice != null && item?.sellingPrice != null) {
        const diff = Math.max(item.listPrice - item.sellingPrice, 0);
        return diff ? parseFloat((diff / 100).toFixed(2)) : 0;
      }
      return null;
    },

    // 2. listPrice - price
    () => {
      if (item?.listPrice != null && item?.price != null) {
        return Math.max(item.listPrice - item.price, 0);
      }
      return null;
    },

    // 3. desconto calculado.
    () => (item?.discount || item?.discount === 0 ? item.discount : null),
  ];

  for (const getValue of strategies) {
    const value = getValue();

    if (value !== null && value !== undefined) {
      return value;
    }
  }

  return 0;
}

export function getItemCategory(item: any) {
  const keywords = ["outlet", "bazar", "sale"];
  const regex = new RegExp(keywords.join("|"), "i"); // cria: /outlet|bazar|sale/i

  const strategies = [
    () => item?.item_category,
    () => item?.category,
    () => item?.categories, 
    () => item?.productCategories?.["39"],
    () => {
      /* 
      Regra: percorre o array itemListElement buscando a categoria de nível raiz,
      identificada por URLs no formato "/slug/" (apenas um nível, ex: "/bazar/", "/new-collection/").
      Retorna o `name` correspondente a esse item.
      */
      const breadcrumb = item?.itemOffered?.breadcrumbList?.itemListElement;

      if (!Array.isArray(breadcrumb)) return null;

      const level1 = breadcrumb.find((el) => {
        const path = el?.item;
        if (!path) return false;

        // remove "/" do início/fim e divide
        const parts = path.replace(/^\/|\/$/g, '').split('/');

        return parts.length === 1;
      });

      return level1?.name || null;
    }
  ];

  for (const getValue of strategies) {
    const rawValue = getValue();

    if (rawValue) {
      const textToTest = Array.isArray(rawValue) ? rawValue.join(" ") : String(rawValue);
      const match = textToTest.match(regex);

      if (match) {
        return match[0].toLowerCase();
      }
    }
  }
  return "Coleção";
}

export function getItemCategory2(item: any) {
  const strategies = [
    () => item?.item_category2, 
    () => {
      const last = Object.values(item?.productCategories || item?.categories || {}).pop() as string;
      return last?.split('/')?.filter(Boolean)?.pop(); // "/Roupas/Malha/" -> "Malha"
    },
    () => item?.category?.split?.('>')?.[1]?.trim?.(), // deco select_item context
    () => item?.subGrup,
  ];

  for (const getValue of strategies) {
    const value = getValue();

    if (value !== null && value !== undefined) {
      return value;
    }
  }

  return null;
}

export function getItemCategory4(item: any) {
  const tags = item?.additionalInfo?.tags || item?.tags;
  return Array.isArray(tags) && tags.length ? tags.join(',') : null;
}

const BRAND_CODES = {
  farm: 'farmrio',
  'farm etc': 'farmetc',
  'farm rio': 'farmrio',
} as const;

type Standardized = keyof typeof BRAND_CODES
interface BrandItem {
  brand?: string;
  additionalInfo?: {
    brandName?: string;
  };
}

/**
 * Extrai o código padronizado (ex: 'farmrio') de uma única string de marca.
 * Retorna null se não houver mapeamento.
 */
function getBrandCode(brand: string | undefined): string | null {
  if (!brand) return null;
  const standardized = brand.toLowerCase().trim();
  return BRAND_CODES[standardized as Standardized] || null;
}

/**
 * Função principal que padroniza a marca ou lista de marcas para o formato final:
 * 'farmrio', 'farmetc', ou 'farmrio_farmetc'.
 */
export function normalizeBrand(input: string | BrandItem[] | BrandItem | string[] | undefined | null) {
  let brandStrings = [];

  if (Array.isArray(input) && input.every(item => typeof item === 'string')) {
    brandStrings = input as string[];
  } else if (Array.isArray(input)) {
    // Se for um Array, coleta a string da marca de cada item.
    brandStrings = input.map(item => item.additionalInfo?.brandName || item.brand);
  } else if (typeof input === 'object' && input !== null && 'brand' in input) {
    // Se for um Objeto único, trata como um Array de um único item (para reuso de lógica).
    brandStrings = [(input as BrandItem).additionalInfo?.brandName || (input as BrandItem).brand];
  } else if (typeof input === 'string') {
    // Se for uma String (mal formatada ou não), trata diretamente.
    return getBrandCode(input);
  } else {
    // Se for undefined, null, ou outro tipo não esperado.
    return null;
  }

  const brandCodes = brandStrings
    .map(getBrandCode)
    .filter((code): code is string => code !== null); // ['farmrio', 'farmetc', 'farmrio']

  const uniqueSortedCodes = [...new Set(brandCodes)].sort(); // ['farmetc', 'farmrio']

  // Retorna a combinação ('farmrio', 'farmetc', ou 'farmrio_farmetc').
  return uniqueSortedCodes.length > 0 ? uniqueSortedCodes.join('_') : null;
}
